/**
 * Service layer for Address Lookup.
 * This service centralizes the state, orchestration, or data access used by its feature area.
 */
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { AddressDetails, DEFAULT_ADDRESS_DETAILS } from '../../shared/models/address.types';

interface NominatimAddress {
  city?: string;
  city_district?: string;
  country?: string;
  county?: string;
  municipality?: string;
  neighbourhood?: string;
  pedestrian?: string;
  postcode?: string;
  quarter?: string;
  region?: string;
  road?: string;
  state?: string;
  suburb?: string;
  town?: string;
  village?: string;
}

interface NominatimResult {
  display_name: string;
  lat: string;
  lon: string;
  address?: NominatimAddress;
}

export interface AddressLookupOption {
  label: string;
  value: string;
  caption: string;
  structured?: AddressDetails;
}

@Injectable({ providedIn: 'root' })
export class AddressLookupService {
  private readonly http = inject(HttpClient);

  async search(query: string): Promise<AddressLookupOption[]> {
    const trimmedQuery = query.trim();

    if (trimmedQuery.length < 3) {
      return [];
    }

    const params = new HttpParams()
      .set('format', 'json')
      .set('addressdetails', '1')
      .set('limit', '5')
      .set('q', trimmedQuery);

    const response = await firstValueFrom(
      this.http.get<NominatimResult[]>('https://nominatim.openstreetmap.org/search', { params })
    );

    return response.map((item) => ({
      label: item.display_name,
      value: item.display_name,
      caption: this.buildCaption(item),
      structured: this.mapAddress(item)
    }));
  }

  async getStructuredAddress(query: string): Promise<AddressDetails | null> {
    const trimmedQuery = query.trim();

    if (!trimmedQuery) {
      return null;
    }

    const params = new HttpParams()
      .set('format', 'json')
      .set('addressdetails', '1')
      .set('limit', '1')
      .set('q', trimmedQuery);

    const response = await firstValueFrom(
      this.http.get<NominatimResult[]>('https://nominatim.openstreetmap.org/search', { params })
    );

    const first = response[0];

    if (!first) {
      return null;
    }

    return this.mapAddress(first);
  }

  private buildCaption(result: NominatimResult): string {
    const locality = result.address?.city ?? result.address?.town ?? result.address?.village ?? result.address?.municipality ?? '';
    const region = result.address?.state ?? result.address?.region ?? '';
    return [locality, region].filter(Boolean).join(', ');
  }

  private mapAddress(result: NominatimResult): AddressDetails {
    const details = result.address ?? {};

    return {
      ...DEFAULT_ADDRESS_DETAILS,
      formattedAddress: result.display_name,
      streetAddress: details.road ?? details.pedestrian ?? details.neighbourhood ?? details.suburb ?? '',
      locality: details.city ?? details.town ?? details.village ?? details.municipality ?? '',
      subLocality: details.suburb ?? details.neighbourhood ?? details.quarter ?? '',
      region: details.state ?? details.region ?? '',
      country: details.country ?? '',
      postalCode: details.postcode ?? '',
      area: details.city_district ?? details.county ?? details.suburb ?? details.neighbourhood ?? '',
      geo: [Number(result.lon), Number(result.lat)]
    };
  }
}
